import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowForward, Lock } from '@mui/icons-material'
import { Box, Link, SxProps, Theme, Typography } from '@mui/material'

import { BlueprintFamilyExampleImage, resolveExampleImageGameName } from '../../api/blueprintFamilies/exampleImages'
import { useLiveEventsTrackerAccessCheck } from '../../features/account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import GameIcon from '../../features/game/components/GameIcon/GameIcon'
import { EventDialogTab } from '../../features/live-events/hooks/useEventDialogTabs'
import { useExampleImageGame } from '../../features/live-events/hooks/useExampleImageGame'
import { useLiveEventModal } from '../../features/live-events/hooks/useLiveEventModal'
import { useLockedGameDialog } from '../../features/live-events/hooks/useLockedGameDialog'
import { useCurrentMarket } from '../../features/markets'
import { LockedGameSummary } from '../../types/LockedFeature'
import { GRTooltip } from '../GRTooltip/GRTooltip'

type LiveEventSourceCaptionProps = {
  image: BlueprintFamilyExampleImage
  // `body1` / `body2` in a lightbox, where there is room; `caption` under a carousel slide.
  variant?: 'caption' | 'body1' | 'body2'
  styles?: SxProps<Theme>
  /**
   * Prefixes the caption with the source game's icon. Opt-in (rather than the
   * default for every caller) because it costs a `getGames` fetch per distinct
   * `gameId` and some callers reserve a fixed-height row too tight to grow into.
   */
  showGameIcon?: boolean
  /**
   * Renders the game name and event name as two stacked lines instead of one
   * `Game · Event` line. Opt-in: most callers sit in a single fixed-height row (a
   * carousel footer, a table cell) where a second line would blow the layout; this
   * is for callers with room to spare, like the Data Glossary gallery card, where
   * each name gets the caption's full width to itself instead of splitting one line
   * and truncating the event name down to whatever the game name left behind.
   */
  stacked?: boolean
  /**
   * Skips the hover tooltip. For callers that already show both names in full (an
   * enlarged view with room to spare), where the tooltip would only repeat them.
   * Ignored for a game outside the user's plan: the tooltip is where the lock is explained.
   */
  hideTooltip?: boolean
  /**
   * Opt-in: called instead of the caption's own unlock dialog whenever the caption shows the
   * lock, so a surface can route it to the dialog it already mounts. Also turns the plain-text
   * caption a reader without the Live Events Tracker role gets into a button calling it.
   */
  onLockedClick?: (event: React.SyntheticEvent, game?: LockedGameSummary) => void
}

/**
 * "Which game and event is this screenshot from?" — the line under an analyst-curated
 * example screenshot on the Blueprint Family surfaces (Live Event Analytics and Data
 * Glossary).
 *
 * Opens the event dialog in place, on the very screenshot the reader clicked, rather
 * than navigating to the Live Events Tracker: `screenshotIndex` is resolved by the BE
 * against the event's current screenshots, which is the whole reason the analyst gets
 * to pick a specific shot.
 *
 * Without the Live Events Tracker role the names still render, as plain text. Linking
 * there would land the reader on a locked page, which is the same reason the news feed
 * and `LiveEventScreenshotSlider` suppress their own event links.
 *
 * The game name comes as a per-market map and is resolved here in the reader's
 * language. A game with no names on record has none, and the caption then carries
 * the event name alone rather than an invented placeholder.
 */
export const LiveEventSourceCaption: FC<LiveEventSourceCaptionProps> = ({
  image,
  variant = 'caption',
  styles,
  showGameIcon,
  stacked,
  hideTooltip,
  onLockedClick,
}) => {
  const { t } = useTranslation()
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const userLanguage = useCurrentUserLanguage()
  const { showModal: showEventModal } = useLiveEventModal()
  const { currentMarketIso } = useCurrentMarket()
  const { openLockedGameDialog, lockedGameDialog } = useLockedGameDialog()
  // A game outside the user's plan keeps its caption, but clicking it explains the lock
  // instead of opening an event the backend won't serve.
  const locked = image.gameAllowed === false

  const gameName = resolveExampleImageGameName(image, userLanguage)
  const { eventName } = image

  // The source game's icon and publisher (see `useExampleImageGame` for the lookup). Opt-in via
  // `showGameIcon`: it costs a request per distinct `gameId`.
  const wantsGameIcon = Boolean(showGameIcon && image.gameId && currentMarketIso)
  const { game, appType, platforms, lockedGame } = useExampleImageGame(image, Boolean(showGameIcon))

  if (!gameName && !eventName) return null

  const handleClick = (event: React.MouseEvent) => {
    if (locked) {
      ;(onLockedClick ?? openLockedGameDialog)(event, lockedGame)
      return
    }
    showEventModal({
      trackedGameId: image.gameId,
      eventTypeId: image.eventTypeId,
      eventId: image.eventId,
      tab: EventDialogTab.Description,
      screenshotIndex: image.screenshotIndex,
    })
  }

  // Without the Live Events Tracker role every game is outside the user's plan, so the lock shows for them too.
  const showLock = locked || !hasAccessToLiveEventsTracker
  // A flex sibling of the text, never inline inside it: the names are `noWrap`, and an inline
  // icon is the first thing a long name's ellipsis pushes out of view. The caption's own
  // tooltip (or the plain-text branch's one below) explains the lock.
  const lockIcon = showLock ? (
    <Box
      component="span"
      role="img"
      aria-label={t('live-events:locked_game_tooltip')}
      data-testid="locked-game-indicator"
      sx={{ display: 'inline-flex', flexShrink: 0, ml: 'auto', color: 'text.secondary' }}
    >
      <Lock aria-hidden sx={{ fontSize: '1em' }} />
    </Box>
  ) : null

  const label = (
    <>
      {gameName ? (
        <Typography component="span" variant={variant} sx={{ fontWeight: 'bold' }}>
          {gameName}
        </Typography>
      ) : null}
      {gameName && eventName ? (
        <Typography component="span" variant={variant} aria-hidden="true" sx={{ mx: 0.5 }}>
          ·
        </Typography>
      ) : null}
      {eventName ? (
        <Typography component="span" variant={variant}>
          {eventName}
        </Typography>
      ) : null}
    </>
  )

  const lines = (
    <Box sx={{ minWidth: 0 }}>
      {gameName ? (
        <Typography variant={variant} component="div" noWrap sx={{ fontWeight: 'bold' }}>
          {gameName}
        </Typography>
      ) : null}
      {eventName ? (
        <Typography variant={variant} component="div" noWrap>
          {eventName}
        </Typography>
      ) : null}
    </Box>
  )

  // `stacked` gives the icon two lines of text to sit against instead of one, so it
  // steps up a size (`small` over `tiny`) to roughly fill that taller row instead of
  // reading as undersized next to it.
  const iconSize = variant !== 'caption' ? (stacked ? 'small' : 'tiny') : 'xtiny'

  // Mounted as soon as the caller asked for an icon, not once the game lookup lands:
  // `GameIcon` sizes itself from `iconSize` alone and falls back to placeholder art
  // without a `src`, so rendering it up front fills the slot for the duration of the
  // fetch. Waiting for `game` instead left the row iconless and reflowed the names
  // when it arrived — and does the same for a game that never resolves at all.
  //
  // `aria-hidden`: the icon's `alt` text would otherwise double up with the visible
  // game name right next to it in the link/button's computed accessible name.
  const icon = wantsGameIcon ? (
    <Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0 }}>
      <GameIcon
        src={game?.getIcon() as string | undefined}
        gameName={gameName || game?.resolvedName || ''}
        size={iconSize}
        appType={appType}
        gamePlatforms={platforms}
      />
    </Box>
  ) : null

  // The visible caption clips both names to its slide's width, and the shorter the
  // slide the more a long game or event name loses — often the part that identifies
  // it. The tooltip repeats them in full: it is free to be as tall as it needs, and
  // wrapping is the whole point, so nothing here is `noWrap`. `overflowWrap` covers
  // the pathological case of a single unbroken token longer than the tooltip's own
  // max-width, which would otherwise spill past the border instead of wrapping.
  const tooltipContent = (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Fixed `small` regardless of how the caption below sizes its own copy: the
            tooltip always has two lines of text to sit against and room to spare, so
            the size that tracks the caption's cramped variants would read as undersized
            here. Only shown when the caller already fetched the game for the caption —
            this is a restatement of that row, not a reason to fetch on its own. */}
        {game ? (
          <Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0 }}>
            <GameIcon src={game.getIcon() as string} gameName={gameName || game.resolvedName} size="small" appType={appType} gamePlatforms={platforms} />
          </Box>
        ) : null}
        <Box sx={{ overflowWrap: 'anywhere' }}>
          {gameName ? (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {gameName}
            </Typography>
          ) : null}
          {eventName ? <Typography variant="body2">{eventName}</Typography> : null}
        </Box>
      </Box>
      {/* Its own row under the identity block, not a third line beside the icon: the
          names say what this screenshot is, this says what clicking does. Primary
          colour and a trailing arrow are the app's existing "this goes somewhere"
          vocabulary (see the Data Glossary card footer). */}
      <Typography
        variant="body2"
        color="primary"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, fontWeight: 'bold' }}
      >
        {locked ? t('live-events:locked_game_tooltip') : t('common:live_event_source_open_event')}
        {locked ? <Lock fontSize="inherit" /> : <ArrowForward fontSize="inherit" />}
      </Typography>
    </Box>
  )

  if (!hasAccessToLiveEventsTracker) {
    // Every game shows the lock here, so with a handler the whole row explains it on click.
    const lockedButtonProps = onLockedClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: (event: React.MouseEvent) => onLockedClick(event, lockedGame),
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onLockedClick(event, lockedGame)
            }
          },
        }
      : {}
    return (
      <Box
        {...lockedButtonProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
          ...(onLockedClick
            ? {
                cursor: 'pointer',
                '&:focus-visible': { outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              }
            : null),
          ...styles,
        }}
      >
        {icon}
        {stacked ? (
          <Box sx={{ minWidth: 0, color: 'text.secondary' }}>{lines}</Box>
        ) : (
          <Typography variant={variant} component="div" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {label}
          </Typography>
        )}
        {lockIcon ? (
          <GRTooltip content={t('live-events:locked_game_tooltip')} styles={{ display: 'inline-flex', marginLeft: 'auto' }}>
            {lockIcon}
          </GRTooltip>
        ) : null}
      </Box>
    )
  }

  const link = (
    <Link
      component="button"
      type="button"
      onClick={handleClick}
      underline="hover"
      color="text.secondary"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        width: '100%',
        // A Link rendered as a button inherits the browser's centered button text;
        // this caption sits under a left-aligned image, so it follows the image.
        textAlign: 'left',
        ...styles,
      }}
    >
      {icon}
      {stacked ? (
        lines
      ) : (
        <Typography variant={variant} component="span" noWrap sx={{ display: 'block', minWidth: 0 }}>
          {label}
        </Typography>
      )}
      {lockIcon}
    </Link>
  )

  return (
    <>
      {hideTooltip && !locked ? (
        link
      ) : (
        <GRTooltip content={tooltipContent} styles={{ display: 'block', minWidth: 0 }}>
          {link}
        </GRTooltip>
      )}
      {lockedGameDialog}
    </>
  )
}
